import {
  animationFrameScheduler,
  filter,
  fromEvent,
  map,
  subscribeOn,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

export const elementMove = (
  element: HTMLElement,
  handlers: EventHandlers = eventHandlers,
  config: Partial<typeof opts> = opts,
) => {
  let coords: Coords;
  const down$ = fromEvent<PointerEvent>(element, 'pointerdown').pipe(
    filter((event) => event.isPrimary === config.isPrimary),
  );
  const move$ = fromEvent<PointerEvent>(document, 'pointermove');
  const up$ = fromEvent<PointerEvent>(document, 'pointerup');

  return down$.pipe(
    tap((event) => {
      element.setPointerCapture(event.pointerId);
      coords = {
        deltaX: 0,
        deltaY: 0,
        startY: event.clientY,
        startX: event.clientX,
        startRect: element.getBoundingClientRect(),
        event,
      };
      handlers?.start?.(coords);
      event.preventDefault();
      event.stopPropagation();
    }),
    switchMap((startEvent) => {
      const startRect = element.getBoundingClientRect();
      return move$.pipe(
        map((moveEvent) => ({
          deltaX: moveEvent.clientX - startEvent.clientX,
          deltaY: moveEvent.clientY - startEvent.clientY,
          startX: startEvent.clientX,
          startY: startEvent.clientY,
          startRect,
          event: moveEvent,
        })),
        tap((data) => {
          coords = data;
          handlers?.move?.(data);
          data.event.preventDefault();
        }),
        takeUntil(
          up$.pipe(
            tap((event) => {
              element.releasePointerCapture(event.pointerId);
              handlers?.end?.(coords);
            }),
          ),
        ),
      );
    }),
    subscribeOn(animationFrameScheduler),
  );
};

const opts = {
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/PointerEvent/isPrimary) */
  isPrimary: true,
};

type EventHandlers = Partial<typeof eventHandlers>;

export type Coords = {
  deltaX: number;
  deltaY: number;
  startX: number;
  startY: number;
  startRect: DOMRect;
  event: PointerEvent;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const eventHandler = (coords: Coords) => {};

const eventHandlers = {
  start: eventHandler,
  move: eventHandler,
  end: eventHandler,
};
